<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Toggle, Tabs, Tab, Card, Label, Tooltip, Input, Spin, Hint, ColorPicker, Textarea, Select, Button, Icon, Modal, Checkbox, Dropdown, MediaUploaderButton, TimePicker } from '@components'

import { useStaffItem } from '@store/staffItem'
const $staffItem = useStaffItem()

import { useService } from '@store/service'
const $service = useService()

import { useSettings } from '@store/settings'
const $settings = useSettings()

import { toRGB, textColor } from '@utils'

const props = defineProps({
    minimal: {
        type: Boolean,
        default: false,
    }
})
</script>
<template>
    <div v-if="$staffItem.state.loading" class="flex flex-col gap-5 motion-safe:animate-pulse">
        <div class="relative mb-[80px] h-[108px] w-full rounded-lg bg-[#F2F4F7]">
            <div class="bg-[#F2F4F7] absolute top-[29px] left-[50%] -translate-x-2/4 w-[128px] h-[128px] border-8 rounded-full border-white"></div>
        </div>
        <div class="grid grid-cols-3 gap-5">
            <div v-for="i in 3" class="bg-[#F2F4F7] rounded-md h-[69px] w-full"></div>
        </div>
        <div class="grid grid-cols-2 gap-5">
            <div v-for="i in props.minimal ? 2 : 4" class="bg-[#F2F4F7] rounded-md h-[69px] w-full"></div>
        </div>
        <div class="bg-[#F2F4F7] rounded-md h-[90px] w-full" v-if="!props.minimal"></div>
        <div class="bg-[#F2F4F7] rounded-md h-[227px] w-full" v-if="!props.minimal"></div>
    </div>
    <div v-else class="flex flex-col gap-5">
        <div class="relative mb-[80px] h-[108px] w-full rounded-lg" :style="`background-image:url(${Image('staff-cover.svg')});background-color: ${toRGB($staffItem.staff.meta.color, 0.9)};`">
            <div class="absolute bottom-[10px] right-[10px] flex gap-3 items-center">
                <!-- <div class="text-sm font-medium" :style="`color: ${getTextColor($staffItem.staff.meta.color)};`">Calendar color</div> -->
                <ColorPicker v-if="!props.minimal" v-model="$staffItem.staff.meta.color" inputClass="h-[29px] border-none bg-white w-fit !rounded-[50px] !pl-[4px] !pr-[4px] gap-[4px]" colorBoxClass="mt-3"></ColorPicker>
            </div>
            <div class="absolute top-[29px] left-[50%] -translate-x-2/4 w-[128px] h-[128px]">
                <div v-if="$staffItem.staff.meta.profile_image_url" class=" w-[128px] h-[128px] rounded-full overflow-hidden flex items-center justify-center border-8 border-[#fff]" :style="{ 'background-color': $staffItem.staff.meta.color, 'color': textColor($staffItem.staff.meta.color) }">
                    <img :src="$staffItem.staff.meta.profile_image_url" class="h-full w-full rounded-full">
                </div>
                <div v-else-if="$staffItem.staff.display_name" :style="{ 'background-color': $staffItem.staff.meta.color, 'color': textColor($staffItem.staff.meta.color) }" class=" rounded-full border-8 border-[#fff] w-[128px] h-[128px] flex items-center justify-center uppercase font-sans text-5xl font-bold">
                    {{ $staffItem.staff.display_name?.split('')[0] }}
                </div>
                <div v-else class=" w-[128px] h-[128px] rounded-full overflow-hidden flex items-center justify-center border-8 border-[#fff]" :style="{ 'background-color': $staffItem.staff.meta.color, 'color': textColor($staffItem.staff.meta.color) }">
                    <img :src="Image('dummy-profile.png')" class="h-full w-full rounded-full">
                </div>
                <div class="cursor-pointer absolute bottom-0 right-0 border-[3px] border-[#fff] text-[#fff] w-10 h-10 rounded-full flex items-center justify-center bg-[#7F56D9] hover:opacity-95 transition">
                    <MediaUploaderButton name="staff_profile_image" v-model="$staffItem.staff.meta.profile_image_url" :crop="true" :ratio="117 / 128">
                        <Icon name="edit03" size="20px"></Icon>
                    </MediaUploaderButton>
                </div>
            </div>
        </div>
        <div class="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div class="w-full">
                <Label>First Name <div class="text-[#D92D20] inline-block">*</div></Label>
                <Input v-model="$staffItem.staff.meta.first_name" placeholder="Enter first name" type="text" size="sm" class="mt-1.5"></Input>
            </div>
            <div class="w-full">
                <Label>Last Name <div class="text-[#D92D20] inline-block">*</div></Label>
                <Input v-model="$staffItem.staff.meta.last_name" placeholder="Enter last name" type="text" size="sm" class="mt-1.5"></Input>
            </div>
            <div class="w-full">
                <Label>Display Name <div class="text-[#D92D20] inline-block">*</div></Label>
                <Input v-model="$staffItem.staff.display_name" placeholder="Enter display name" type="text" size="sm" class="mt-1.5"></Input>
            </div>
        </div>
        <div class="flex flex-col gap-5 md:grid md:grid-cols-2">
            <div class="w-full" v-if="!props.minimal">
                <Label>Title</Label>
                <Input v-model="$staffItem.staff.meta.title" placeholder="Enter title" type="text" size="sm" class="mt-1.5"></Input>
            </div>
            <div class="w-full" v-if="!props.minimal">
                <Label>Highlights</Label>
                <Input v-model="$staffItem.staff.meta.highlights" placeholder="Enter highlights" type="text" size="sm" class="mt-1.5"></Input>
            </div>
            <div class="w-full">
                <Label>Email Address <div class="text-[#D92D20] inline-block">*</div></Label>
                <Input v-model="$staffItem.staff.meta.email" placeholder="Enter email" type="email" size="sm" class="mt-1.5" iconLeft="mail01"></Input>
            </div>
            <div class="w-full">
                <Label>Phone Number <div class="text-[#D92D20] inline-block">*</div></Label>
                <Input v-model="$staffItem.staff.meta.phone" placeholder="Enter phone number" type="text" size="sm" class="mt-1.5" iconLeft="phoneCall01"></Input>
            </div>
            <div class="w-full col-span-2" v-if="!props.minimal">
                <Label>Bio</Label>
                <Textarea v-model="$staffItem.staff.meta.bio" :autoHeight="true" placeholder="Enter bio" class="mt-1.5"></Textarea>
            </div>
            <!-- <div class="w-full">
                                            <Label>Zoom User ID</Label>
                                            <Input iconLeft="zoom" v-model="$staffItem.staff.meta.zoom_user_id" placeholder="Link" type="text" size="sm" class="mt-1.5"></Input>
                                        </div>
                                        <div class="w-full">
                                            <Label>Timezone <div class="text-[#D92D20] inline-block">*</div></Label>
                                            <Select iconLeft="globe03" :options="timeZones" v-model="$staffItem.staff.meta.timezone" placeholder="UTC" type="text" class="mt-1.5" selectionContainer="py-[10px]"></Select>
                                        </div> -->
            <div class="w-full col-span-2" v-if="!props.minimal">
                <Label>Cover Image</Label>
                <!-- <MediaUploaderButton name="staff_cover_image" v-model="$staffItem.staff.meta.cover_image_url" dummy class="mt-1.5 w-full" /> -->
                <MediaUploaderButton name="staff_cover_image" v-model="$staffItem.staff.meta.cover_images" :dummyThumb="false" dummy multiple class="mt-1.5 w-full" />
            </div>
        </div>
    </div>
</template>


<style>

</style>